/**
 * useWalletInflowSplit — Wallet-Detail "Inflow sources" panel.
 *
 * Klassifiziert die aktuellste Seite eingehender Transaktionen
 * (GET /api/v2/wallets/:id/transactions, bestehender Endpunkt) nach
 * `type` in drei Buckets und gewichtet sie in EUR (uebergebene Live-Preise),
 * damit Tokens unterschiedlicher Groessenordnung (HNT/IOT/MOBILE) vergleichbar
 * bleiben.
 *
 * ponytail: eine Seite (BE-Max 50 Eintraege) statt vollstaendiger 30-Tage-
 * Paginierung — fuer sehr aktive Wallets ist das eine Naeherung, kein exaktes
 * 30-Tage-Total. Upgrade-Pfad: serverseitige Aggregation nach Kategorie.
 */
import { useQuery } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'

export type InflowSplitCategory = 'rewards' | 'transfers' | 'other'

export type InflowSplitEntry = {
  category: InflowSplitCategory
  eurValue: number
  pct: number
}

type RawTx = {
  type?: string
  direction?: string
  token_symbol?: string
  amount?: number
}

function categoryFor(type: string | undefined): InflowSplitCategory {
  if (!type) return 'other'
  if (type.includes('reward')) return 'rewards'
  if (type === 'transfer') return 'transfers'
  return 'other'
}

async function fetchIncomingTransactions(walletId: number): Promise<RawTx[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const client = apiClient as any
  const result = await client.GET('/api/v2/wallets/{id}/transactions', {
    params: { path: { id: walletId }, query: { per_page: 50 } },
  })
  if (result.error) throw result.error
  const raw = result.data as { data?: RawTx[] } | undefined
  return (raw?.data ?? []).filter((tx) => tx.direction === 'in')
}

type Prices = { hnt: number | null; iot: number | null; mobile: number | null }

type UseWalletInflowSplitResult = {
  entries: InflowSplitEntry[]
  isLoading: boolean
  isEmpty: boolean
}

export function useWalletInflowSplit(walletId: number, prices: Prices): UseWalletInflowSplitResult {
  const query = useQuery({
    queryKey: ['wallet', walletId, 'transactions', 'inflow-split'],
    queryFn: () => fetchIncomingTransactions(walletId),
    staleTime: 60_000,
  })

  const priceFor = (symbol: string | undefined): number => {
    switch (symbol?.toUpperCase()) {
      case 'HNT':
        return prices.hnt ?? 0
      case 'IOT':
        return prices.iot ?? 0
      case 'MOBILE':
        return prices.mobile ?? 0
      default:
        return 0
    }
  }

  const sums: Record<InflowSplitCategory, number> = { rewards: 0, transfers: 0, other: 0 }
  for (const tx of query.data ?? []) {
    const cat = categoryFor(tx.type)
    sums[cat] += (tx.amount ?? 0) * priceFor(tx.token_symbol)
  }
  const total = sums.rewards + sums.transfers + sums.other

  const order: InflowSplitCategory[] = ['rewards', 'transfers', 'other']
  const entries: InflowSplitEntry[] = order
    .map((category) => ({
      category,
      eurValue: sums[category],
      pct: total > 0 ? (sums[category] / total) * 100 : 0,
    }))
    .filter((e) => e.eurValue > 0)

  return {
    entries,
    isLoading: query.isLoading,
    isEmpty: !query.isLoading && total === 0,
  }
}
